/**
 * InsuraHelp Service
 * Handles API calls for the InsuraHelp AI assistant
 */

import axiosInstance from './axiosConfig';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface InsuraHelpContext {
  currentPath: string;
  userType: string;
  accountType: string;
  accountStatus?: string;
  hasAgency: boolean;
  agencyId?: string;
  agencyRole?: string;
  userName?: string;
  isTrialUser?: boolean;
  isTrialActive?: boolean;
}

interface InsuraHelpResponse {
  message: string;
  navigateTo?: string;
}

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
}

/**
 * Send a chat message to InsuraHelp AI
 */
export async function chat(
  message: string,
  context: InsuraHelpContext,
  history: ChatMessage[] = [],
): Promise<InsuraHelpResponse> {
  try {
    const response = await axiosInstance.post<ApiResponse<InsuraHelpResponse>>('/api/insura-help/chat', {
      message,
      context,
      history,
      stream: false,
    });

    // Handle various response structures
    const data = response.data?.data || response.data;

    if (!data || typeof data !== 'object') {
      console.error('InsuraHelp: Invalid response structure', response.data);
      return {
        message: "I'm sorry, I couldn't process that request. Please try again.",
      };
    }

    // If data has message property, use it; otherwise check if data itself is the message
    if ('message' in data && typeof data.message === 'string') {
      return data as InsuraHelpResponse;
    }

    console.error('InsuraHelp: Missing message in response', data);
    return {
      message: "I'm sorry, I encountered an issue. Please try again.",
    };
  } catch (error: any) {
    console.error('InsuraHelp API error:', error?.response?.data || error?.message || error);
    throw error;
  }
}

/**
 * Stream chat response from InsuraHelp AI
 * Returns an async generator that yields text chunks
 */
export async function* chatStream(
  message: string,
  context: InsuraHelpContext,
  history: ChatMessage[] = [],
): AsyncGenerator<{ type: 'chunk' | 'navigate' | 'truncated' | 'done'; content?: string; path?: string }> {
  const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/insura-help/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${await getAccessToken()}`,
    },
    body: JSON.stringify({
      message,
      context,
      history,
      stream: true,
    }),
  });

  if (!response.ok) {
    throw new Error(`InsuraHelp request failed: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          yield data;
        } catch {
          // Skip invalid JSON
        }
      }
    }
  }
}

/**
 * Get the current access token for streaming requests
 */
async function getAccessToken(): Promise<string> {
  // Import dynamically to avoid circular dependencies
  const supabaseAuthService = (await import('./supabaseAuthService')).default;
  return await supabaseAuthService.getAccessToken() || '';
}

/**
 * Health check for InsuraHelp service
 */
export async function healthCheck(): Promise<boolean> {
  try {
    await axiosInstance.post('/api/insura-help/health');
    return true;
  } catch {
    return false;
  }
}

const insuraHelpService = {
  chat,
  chatStream,
  healthCheck,
};

export default insuraHelpService;
